import { useEffect, useRef, useState } from 'react';
import type { Deck, Member } from '../types';
import { EmojiPicker } from './EmojiPicker';

type Props = {
  members: Member[];
  deck: Deck | undefined;
  recentEmoji: string[];
  revealed: boolean;
  youId: string | null;
  isHost: boolean;
  onKick: (memberId: string) => void;
  onTransferHost: (memberId: string) => void;
  onThrow: (memberId: string, emoji: string) => void;
};

const labelFor = (deck: Deck | undefined, cardId: string | null): string =>
  deck?.cards.find((card) => card.id === cardId)?.label ?? '?';

const ThrowMenu = ({
  recentEmoji,
  onPick,
  onBrowseAll,
  onClose,
}: {
  recentEmoji: string[];
  onPick: (emoji: string) => void;
  onBrowseAll: () => void;
  onClose: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div className="throw-menu" ref={ref} role="menu">
      <div className="throw-grid">
        {recentEmoji.map((emoji) => (
          <button
            key={emoji}
            type="button"
            role="menuitem"
            className="throw-option"
            aria-label={`Throw ${emoji}`}
            onClick={() => onPick(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
      <button type="button" className="throw-more" onClick={onBrowseAll}>
        <span aria-hidden="true">😀</span> All emoji…
      </button>
    </div>
  );
};

type SeatProps = {
  member: Member;
  deck: Deck | undefined;
  recentEmoji: string[];
  revealed: boolean;
  isYou: boolean;
  canManage: boolean;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onBrowseAll: () => void;
  onKick: (memberId: string) => void;
  onTransferHost: (memberId: string) => void;
  onThrow: (memberId: string, emoji: string) => void;
};

const Seat = ({
  member,
  deck,
  recentEmoji,
  revealed,
  isYou,
  canManage,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
  onBrowseAll,
  onKick,
  onTransferHost,
  onThrow,
}: SeatProps) => {
  const state = member.spectator
    ? 'spectator'
    : revealed && member.vote
      ? 'revealed'
      : member.hasVoted
        ? 'voted'
        : 'waiting';

  return (
    <li
      className={`seat seat-${state}${member.connected ? '' : ' seat-away'}`}
      data-seat={member.id}
    >
      <div className="seat-card">
        {state === 'revealed' && <span className="seat-value">{labelFor(deck, member.vote)}</span>}
        {state === 'spectator' && <span className="seat-eye">👁</span>}
      </div>

      <div className="seat-name">
        <span className="seat-name-text" title={member.name}>
          {member.name}
        </span>
        {isYou && <span className="tag">you</span>}
        {member.isHost && <span className="tag tag-host">host</span>}
        {!member.connected && <span className="tag tag-away">away</span>}
      </div>

      <div className="seat-actions">
        <button
          type="button"
          className="icon-btn"
          title={isYou ? 'Throw something at yourself' : `Throw something at ${member.name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={onToggleMenu}
        >
          {recentEmoji[0] ?? '🍅'}
        </button>
        {canManage && (
          <>
            {!member.isHost && (
              <button
                type="button"
                className="icon-btn"
                title={`Make ${member.name} the host`}
                onClick={() => onTransferHost(member.id)}
              >
                ★
              </button>
            )}
            <button
              type="button"
              className="icon-btn icon-btn-danger"
              title={`Remove ${member.name}`}
              onClick={() => onKick(member.id)}
            >
              ✕
            </button>
          </>
        )}
      </div>

      {menuOpen && (
        <ThrowMenu
          recentEmoji={recentEmoji}
          onClose={onCloseMenu}
          onBrowseAll={onBrowseAll}
          onPick={(emoji) => {
            onThrow(member.id, emoji);
            onCloseMenu();
          }}
        />
      )}
    </li>
  );
};

export const Table = ({
  members,
  deck,
  recentEmoji,
  revealed,
  youId,
  isHost,
  onKick,
  onTransferHost,
  onThrow,
}: Props) => {
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const [browsingFor, setBrowsingFor] = useState<Member | null>(null);

  return (
    <>
      <ul className="table">
        {members.map((member) => (
          <Seat
            key={member.id}
            member={member}
            deck={deck}
            recentEmoji={recentEmoji}
            revealed={revealed}
            isYou={member.id === youId}
            canManage={isHost && member.id !== youId}
            menuOpen={openMenuFor === member.id}
            onToggleMenu={() =>
              setOpenMenuFor((current) => (current === member.id ? null : member.id))
            }
            onCloseMenu={() => setOpenMenuFor(null)}
            onBrowseAll={() => {
              setOpenMenuFor(null);
              setBrowsingFor(member);
            }}
            onKick={onKick}
            onTransferHost={onTransferHost}
            onThrow={onThrow}
          />
        ))}
      </ul>

      {browsingFor && (
        <EmojiPicker
          targetName={browsingFor.name}
          onClose={() => setBrowsingFor(null)}
          onPick={(emoji) => {
            onThrow(browsingFor.id, emoji);
            setBrowsingFor(null);
          }}
        />
      )}
    </>
  );
};
